import { AfterViewInit, Component, ElementRef, HostListener, Inject, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { StammdatenService } from '../../stammdaten.service';
import { MessageService } from '../../../../services/message-service/message.service';
import { Haendler } from '../haendler';
import { ActivatedRoute, Router } from '@angular/router';
import { debounceTime, distinctUntilChanged, Subscription } from 'rxjs';
import { AppService } from '../../../../services/app/app.service';
import { MatDialog } from '@angular/material/dialog';
import { HaendlerDetailsDialogComponent } from '../haendler-details-dialog/haendler-details-dialog.component';
import { ConfirmDialogComponent } from '../../../dialogs/confirm-dialog/confirm-dialog.component';
import { ConfirmDialogInput, ConfirmDialogResult } from '../../../dialogs/confirm-dialog/confirm-dialog';
import { Benutzerprofil } from '../../../stammdaten/mein-profil/mein-profil';
import { KieneBackendApiService } from '../../../../services/backend-api/kiene-backend-api.service';
import { API_BASE_URL_SERVICE } from '../../../../kiene-core.config';
import { LocalStorageService } from '../../../../services/local-storage/local-storage.service';
import { canEditHaendler as userCanEditHaendler } from '../haendler-permissions';

@Component({
    selector: 'kiene-haendler-uebersicht-favoriten-alt',
    templateUrl: './haendler-uebersicht-favoriten-alt.component.html',
    styleUrls: ['./haendler-uebersicht-favoriten-alt.component.scss'],
})
export class HaendlerUebersichtFavoritenAltComponent implements OnInit, OnDestroy, AfterViewInit {
    benutzerprofil = new Benutzerprofil();
    favoritenHaendler: Haendler[];
    gefilterteHaendler: Haendler[] = [];
    haendlerFehlt = this.stammdatenService.getHaendlerFehlt();
    watchHaendlerFehltSub: Subscription;
    haendlerFavoritenSubscription: Subscription;
    searchSubscription: Subscription;
    @Input('showReturnButton') showReturnButton = true;
    searchControl = new FormControl();
    columns: { key: string; label: string; widthPx: number; alwaysVisible?: boolean; visible: boolean }[] = [
        { key: 'name', label: 'Händler', widthPx: 220, alwaysVisible: true, visible: true },
        { key: 'vvvo', label: 'VVVO', widthPx: 220, visible: true },
        { key: 'email', label: 'E-Mail', widthPx: 320, visible: true },
        { key: 'telefon', label: 'Telefon', widthPx: 180, visible: true },
        { key: 'mobil', label: 'Mobil', widthPx: 180, visible: true },
        { key: 'fax', label: 'FAX', widthPx: 170, visible: true },
        { key: 'adresse', label: 'Adresse', widthPx: 420, visible: true },
    ];
    readonly actionColumnWidthPx = 44;
    readonly desktopColumnGapPx = 16;
    horizontalScrollWidth = 0;
    showHorizontalScrollbar = false;
    bottomScrollbarLeft = 0;
    bottomScrollbarWidth = 0;
    private syncFromTable = false;
    private syncFromBottom = false;
    private tableResizeObserver?: ResizeObserver;
    private tableVisibilityObserver?: IntersectionObserver;
    private syncTimeoutHandles: number[] = [];
    private initialSyncIntervalHandle?: number;
    @ViewChild('tableScrollContainer') tableScrollContainer: ElementRef<HTMLDivElement>;
    @ViewChild('bottomScrollbar') bottomScrollbar: ElementRef<HTMLDivElement>;
    canEditHaendler = false;

    constructor(
        private readonly stammdatenService: StammdatenService,
        private readonly messageService: MessageService,
        private readonly router: Router,
        private readonly route: ActivatedRoute,
        private readonly dialog: MatDialog,
        private readonly appService: AppService,
        private readonly api: KieneBackendApiService,
        @Inject(API_BASE_URL_SERVICE) private readonly apiBaseUrl: string,
        private readonly localStorageService: LocalStorageService,
    ) {}

    ngOnInit(): void {
        this.canEditHaendler = userCanEditHaendler(this.localStorageService);
        this.ladeBenutzerprofil();
        this.ladeFavoriten();

        this.watchHaendlerFehltSub = this.stammdatenService.watchHaendlerFehlt().subscribe(fehlt => {
            this.haendlerFehlt = fehlt;
        });

        this.searchSubscription = this.searchControl.valueChanges.pipe(debounceTime(150), distinctUntilChanged()).subscribe(() => {
            this.applyFrontendSearch();
            this.scheduleHorizontalScrollbarSync();
        });
    }

    ngAfterViewInit(): void {
        this.initTableResizeObserver();
        this.initTableVisibilityObserver();
        this.startInitialSyncLoop();
    }

    ngOnDestroy(): void {
        this.watchHaendlerFehltSub?.unsubscribe();
        this.haendlerFavoritenSubscription?.unsubscribe();
        this.searchSubscription?.unsubscribe();
        this.tableResizeObserver?.disconnect();
        this.tableVisibilityObserver?.disconnect();
        this.clearScheduledScrollbarSync();
        this.stopInitialSyncLoop();
    }

    ladeFavoriten(): void {
        this.haendlerFavoritenSubscription?.unsubscribe();
        this.haendlerFavoritenSubscription = this.stammdatenService
            .loadHaendler()
            .subscribe({
                next: data => {
                    this.favoritenHaendler = [];
                    this.favoritenHaendler = data.records;
                    this.applyFrontendSearch();
                    this.initTableResizeObserver();
                    this.initTableVisibilityObserver();
                    this.startInitialSyncLoop();
                },
                error: (err: any) => {
                    this.messageService.errorMessage('Fehler beim Laden aller Händler', err);
                },
            });
    }

    ladeBenutzerprofil() {
        this.api.get(`${this.apiBaseUrl}benutzerprofil/read_one_kundenportal.php`).subscribe({
            next: (response: Benutzerprofil) => {
                this.benutzerprofil = response;
            },
            error: err => {
                this.messageService.errorMessage('Profil konnte nicht geladen werden!', err);
            },
        });
    }

    editiereHaendler(haendler: Haendler): void {
        if (!this.canEditHaendler) {
            return;
        }
        if (!this.appService.isMobileScreen) {
            const dialogRef = this.dialog.open(HaendlerDetailsDialogComponent, {
                minWidth: '600px',
                data: { haendler: haendler },
            });
            dialogRef.afterClosed().subscribe({
                next: () => {
                    this.ladeFavoriten();
                },
            });
        } else {
            this.router.navigate(['details', haendler.viehhaendler_id], { relativeTo: this.route }).then();
        }
    }

    anlegenHaendler(): void {
        if (!this.canEditHaendler) {
            return;
        }
        if (this.appService.isMobileScreen) {
            this.router.navigate(['details'], { relativeTo: this.route }).then();
        } else {
            const dialogRef = this.dialog.open(HaendlerDetailsDialogComponent, { minWidth: '600px' });
            dialogRef.afterClosed().subscribe({
                next: () => {
                    this.ladeFavoriten();
                },
            });
        }
    }

    goBack(): void {
        window.history.back();
    }

    sendMail(haendler: Haendler) {
        if (haendler.email) {
            const htmlAnchorElement = document.createElement('a');
            htmlAnchorElement.style.display = 'none';
            htmlAnchorElement.href = 'mailto:' + haendler.email;
            document.body.appendChild(htmlAnchorElement);
            htmlAnchorElement.click();
        }
    }

    deleteHaendler(h: Haendler) {
        if (!this.canEditHaendler) {
            return;
        }
        const input = new ConfirmDialogInput();
        input.headline = 'Händler löschen';
        input.text = 'Wollen Sie den Händler ' + h.name + ' wirklich löschen?';
        input.okButton = 'Löschen';
        input.buttonColor = 'warn';
        const dialogRef = this.dialog.open(ConfirmDialogComponent, { data: input });
        dialogRef.afterClosed().subscribe({
            next: (result: ConfirmDialogResult) => {
                if (result.ok && result.confirmed) {
                    this.stammdatenService.deleteHaendlerFavorit(h).subscribe({
                        next: () => {
                            this.messageService.infoMessage('Der Händler wurde erfolgreich entfernt!');
                            this.ladeFavoriten();
                        },
                        error: err => {
                            this.messageService.errorMessage('Fehler!', err);
                        },
                    });
                }
            },
        });
    }

    public clearSearch(): void {
        this.searchControl.setValue('');
    }

    private applyFrontendSearch(): void {
        const list = this.favoritenHaendler ?? [];
        const query = (this.searchControl.value ?? '').toString().trim().toLowerCase();
        if (!query) {
            this.gefilterteHaendler = [...list];
            return;
        }
        this.gefilterteHaendler = list.filter(h => {
            const name = this.getCellValue(h, 'name').toLowerCase();
            const vvvo = this.getCellValue(h, 'vvvo').toLowerCase();
            const email = this.getCellValue(h, 'email').toLowerCase();
            const telefon = this.getCellValue(h, 'telefon').toLowerCase();
            const mobil = this.getCellValue(h, 'mobil').toLowerCase();
            const fax = this.getCellValue(h, 'fax').toLowerCase();
            const adresse = this.getCellValue(h, 'adresse').toLowerCase();
            return (
                name.includes(query) ||
                vvvo.includes(query) ||
                email.includes(query) ||
                telefon.includes(query) ||
                mobil.includes(query) ||
                fax.includes(query) ||
                adresse.includes(query)
            );
        });
    }

    public getVisibleColumns() {
        return this.columns.filter(col => col.visible);
    }

    public canToggleColumn(col: { alwaysVisible?: boolean }) {
        return !col.alwaysVisible;
    }

    public toggleColumn(key: string) {
        const col = this.columns.find(c => c.key === key);
        if (!col || !this.canToggleColumn(col)) {
            return;
        }
        col.visible = !col.visible;
        this.scheduleHorizontalScrollbarSync();
    }

    public getGridTemplate(): string {
        const visible = this.getVisibleColumns().map(col => `minmax(${this.getColumnMinWidthPx(col.key)}px, 1fr)`);
        return [...visible, '44px'].join(' ');
    }

    public getDesktopMinWidthPx(): number {
        const contentWidth = this.getVisibleColumns().reduce((sum, col) => sum + this.getColumnMinWidthPx(col.key), 0);
        const visibleColumnCount = this.getVisibleColumns().length;
        const totalGaps = Math.max(visibleColumnCount, 0) * this.desktopColumnGapPx;
        return contentWidth + this.actionColumnWidthPx + totalGaps;
    }

    public getCellValue(h: Haendler, key: string): string {
        if (key === 'adresse') {
            const strasse = (h?.strasse || '').toString().trim();
            const plz = (h?.plz || '').toString().trim();
            const ort = (h?.ort || '').toString().trim();
            const secondLine = `${plz}${plz && ort ? ' ' : ''}${ort}`.trim();
            const value = `${strasse}${strasse && secondLine ? ', ' : ''}${secondLine}`.trim();
            return value || '-';
        }
        const value = (h as any)?.[key];
        if (value === null || value === undefined || value === '') {
            return '-';
        }
        return String(value);
    }

    public get isMobileScreen(): boolean {
        return this.appService.isMobileScreen;
    }

    public forceHorizontalScrollbarSync(): void {
        this.startInitialSyncLoop();
    }

    onTableHorizontalScroll() {
        if (this.syncFromBottom) {
            return;
        }
        const tableEl = this.tableScrollContainer?.nativeElement;
        const bottomEl = this.bottomScrollbar?.nativeElement;
        if (!tableEl || !bottomEl) {
            return;
        }
        this.syncFromTable = true;
        bottomEl.scrollLeft = tableEl.scrollLeft;
        this.syncFromTable = false;
    }

    onBottomScrollbarScroll() {
        if (this.syncFromTable) {
            return;
        }
        const tableEl = this.tableScrollContainer?.nativeElement;
        const bottomEl = this.bottomScrollbar?.nativeElement;
        if (!tableEl || !bottomEl) {
            return;
        }
        this.syncFromBottom = true;
        tableEl.scrollLeft = bottomEl.scrollLeft;
        this.syncFromBottom = false;
    }

    @HostListener('window:resize')
    onWindowResize() {
        this.syncHorizontalScrollbar();
    }

    private syncHorizontalScrollbar() {
        const tableEl = this.tableScrollContainer?.nativeElement;
        const bottomEl = this.bottomScrollbar?.nativeElement;
        if (!tableEl || !bottomEl) {
            return;
        }
        const rect = tableEl.getBoundingClientRect();
        this.bottomScrollbarLeft = rect.left;
        this.bottomScrollbarWidth = rect.width;
        this.horizontalScrollWidth = tableEl.scrollWidth;
        this.showHorizontalScrollbar = tableEl.scrollWidth > tableEl.clientWidth;
        bottomEl.scrollLeft = tableEl.scrollLeft;
    }

    private initTableResizeObserver() {
        const tableEl = this.tableScrollContainer?.nativeElement;
        if (!tableEl || typeof ResizeObserver === 'undefined') {
            return;
        }
        this.tableResizeObserver?.disconnect();
        this.tableResizeObserver = new ResizeObserver(() => this.syncHorizontalScrollbar());
        this.tableResizeObserver.observe(tableEl);
    }

    private initTableVisibilityObserver() {
        const tableEl = this.tableScrollContainer?.nativeElement;
        if (!tableEl || typeof IntersectionObserver === 'undefined') {
            return;
        }
        this.tableVisibilityObserver?.disconnect();
        this.tableVisibilityObserver = new IntersectionObserver(entries => {
            const isVisible = entries.some(entry => entry.isIntersecting);
            if (isVisible) {
                this.startInitialSyncLoop();
            }
        });
        this.tableVisibilityObserver.observe(tableEl);
    }

    private scheduleHorizontalScrollbarSync(): void {
        this.clearScheduledScrollbarSync();
        const delays = [0, 80, 220];
        this.syncTimeoutHandles = delays.map(delay =>
            window.setTimeout(() => {
                this.syncHorizontalScrollbar();
            }, delay),
        );
    }

    private clearScheduledScrollbarSync(): void {
        for (const timeoutHandle of this.syncTimeoutHandles) {
            clearTimeout(timeoutHandle);
        }
        this.syncTimeoutHandles = [];
    }

    private startInitialSyncLoop(): void {
        this.scheduleHorizontalScrollbarSync();
        this.stopInitialSyncLoop();
        let attemptsLeft = 20;
        this.initialSyncIntervalHandle = window.setInterval(() => {
            this.syncHorizontalScrollbar();
            attemptsLeft--;
            if (attemptsLeft <= 0) {
                this.stopInitialSyncLoop();
            }
        }, 100);
    }

    private stopInitialSyncLoop(): void {
        if (this.initialSyncIntervalHandle !== undefined) {
            clearInterval(this.initialSyncIntervalHandle);
            this.initialSyncIntervalHandle = undefined;
        }
    }

    private getColumnMinWidthPx(key: string): number {
        switch (key) {
            case 'name':
                return 150;
            case 'vvvo':
                return 140;
            case 'email':
                return 200;
            case 'telefon':
            case 'mobil':
            case 'fax':
                return 120;
            case 'adresse':
                return 260;
            default:
                return 120;
        }
    }
}
